import React, { Suspense, lazy } from 'react'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { ProtectedRoute, AdminRoute, GuestRoute } from '@/routes/ProtectedRoute'
import { PageSpinner } from '@/components/ui/Spinner'

// Auth pages (not lazy — small, needed immediately)
import LoginPage from '@/pages/auth/LoginPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import AuthCallbackPage from '@/pages/auth/AuthCallbackPage'
import NotFoundPage from '@/pages/NotFoundPage'

// Lazy-loaded pages
const HomePage = lazy(() => import('@/pages/home/HomePage'))
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'))
const LeadsPage = lazy(() => import('@/pages/leads/LeadsPage'))
const DealsPage = lazy(() => import('@/pages/deals/DealsPage'))
const DealDetail = lazy(() => import('@/pages/deals/DealDetail'))
const PipelinePage = lazy(() => import('@/pages/pipeline/PipelinePage'))
const ContactsPage = lazy(() => import('@/pages/contacts/ContactsPage'))
const ContactDetail = lazy(() => import('@/pages/contacts/ContactDetail'))
const LeadDetail = lazy(() => import('@/pages/leads/LeadDetail'))
const OrganizationsPage = lazy(() => import('@/pages/organizations/OrganizationsPage'))
const WorkspacesPage = lazy(() => import('@/pages/workspaces/Workspaces'))
const OrganizationDetail = lazy(() => import('@/pages/organizations/OrganizationDetail'))
const ActivitiesPage = lazy(() => import('@/pages/activities/ActivitiesPage'))
const NotesPage = lazy(() => import('@/pages/notes/Notes'))
const TasksPage = lazy(() => import('@/pages/tasks/TasksPage'))
const CallsPage = lazy(() => import('@/pages/calls/CallsPage'))
const MeetingsPage = lazy(() => import('@/pages/meetings/MeetingsPage'))
const EmailsPage = lazy(() => import('@/pages/emails/EmailsPage'))
const EmailAccountsPage = lazy(() => import('@/pages/emails/EmailAccountsPage'))
const OutlookCallbackPage = lazy(() => import('@/pages/emails/OutlookCallbackPage'))
const GmailCallbackPage = lazy(() => import('@/pages/emails/GmailCallbackPage'))
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'))
const CalendarPage = lazy(() => import('@/pages/calendar/CalendarPage'))
const WorkflowsPage = lazy(() => import('@/pages/workflows/WorkflowsPage'))
const AutomationRulesPage = lazy(() => import('@/pages/workflows/AutomationRules'))
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'))

// Admin pages
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'))

// Admin pages
const AdminUsersPage = lazy(() => import('@/pages/admin/UsersPage'))
const AdminRolesPage = lazy(() => import('@/pages/admin/RolesPage'))
const AdminTeamsPage = lazy(() => import('@/pages/admin/TeamsPage'))
const AssignmentRulesPage = lazy(() => import('@/pages/admin/AssignmentRulesPage'))
const AdminSettingsPage = lazy(() => import('@/pages/admin/SettingsPage'))
const WorkflowDefinitionsPage = lazy(() => import('@/pages/admin/WorkflowDefinitionsPage'))
const AuditLogsPage = lazy(() => import('@/pages/admin/AuditLogsPage'))
const EmailTemplatesPage = lazy(() => import('@/pages/admin/EmailTemplatesPage'))

function PageLoader() {
  return (
    <div className="flex-1 flex items-center justify-center py-16">
      <PageSpinner />
    </div>
  )
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

/** /organizations -> /crm-organizations, preserving `?workspace=` and any other query.
 *  Exported so the redirect can be tested directly rather than through a copy. */
export function LegacyOrganizationsRedirect() {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: '/crm-organizations', search, hash }} replace />
}

export const router = createBrowserRouter([
  // Root redirect
  {
    path: '/',
    element: <Navigate to="/home" replace />,
  },

  // Guest routes (redirect to dashboard if authenticated)
  {
    element: <GuestRoute />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
      },
    ],
  },

  // Public SSO landing — completes Microsoft login (user not yet authenticated).
  {
    path: '/auth/callback',
    element: <AuthCallbackPage />,
  },

  // Protected routes (require authentication)
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          // Home
          {
            path: '/home',
            element: (
              <SuspenseWrapper>
                <HomePage />
              </SuspenseWrapper>
            ),
          },

          // Dashboard
          {
            path: '/dashboard',
            element: (
              <SuspenseWrapper>
                <DashboardPage />
              </SuspenseWrapper>
            ),
          },

          // Sales
          {
            path: '/leads',
            element: (
              <SuspenseWrapper>
                <LeadsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/leads/:id',
            element: (
              <SuspenseWrapper>
                <LeadDetail />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/deals',
            element: (
              <SuspenseWrapper>
                <DealsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/deals/:id',
            element: (
              <SuspenseWrapper>
                <DealDetail />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/pipeline',
            element: (
              <SuspenseWrapper>
                <PipelinePage />
              </SuspenseWrapper>
            ),
          },

          // Contacts
          {
            path: '/contacts',
            element: (
              <SuspenseWrapper>
                <ContactsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/contacts/:id',
            element: (
              <SuspenseWrapper>
                <ContactDetail />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/crm-organizations',
            element: (
              <SuspenseWrapper>
                <OrganizationsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/crm-organizations/:id',
            element: (
              <SuspenseWrapper>
                <OrganizationDetail />
              </SuspenseWrapper>
            ),
          },
          // Legacy path redirect — keep /organizations working too.
          // Carries the query string across: the selected workspace lives in
          // `?workspace=`, and a bare <Navigate to="/crm-organizations"> dropped
          // it, so following this path silently reset the page to All
          // Workspaces and showed tenant-wide totals.
          {
            path: '/organizations',
            element: <LegacyOrganizationsRedirect />,
          },
          {
            // Not under /admin: sales reps may create and rename workspaces, and
            // the whole /admin section is gated to admins in the sidebar.
            path: '/workspaces',
            element: (
              <SuspenseWrapper>
                <WorkspacesPage />
              </SuspenseWrapper>
            ),
          },

          // Notes
          {
            path: '/notes',
            element: (
              <SuspenseWrapper>
                <NotesPage />
              </SuspenseWrapper>
            ),
          },

          // Activities
          {
            path: '/activities',
            element: (
              <SuspenseWrapper>
                <ActivitiesPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/tasks',
            element: (
              <SuspenseWrapper>
                <TasksPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/calls',
            element: (
              <SuspenseWrapper>
                <CallsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/meetings',
            element: (
              <SuspenseWrapper>
                <MeetingsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/emails',
            element: (
              <SuspenseWrapper>
                <EmailsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/email-accounts',
            element: (
              <SuspenseWrapper>
                <EmailAccountsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/email-accounts/outlook/callback',
            element: (
              <SuspenseWrapper>
                <OutlookCallbackPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/email-accounts/gmail/callback',
            element: (
              <SuspenseWrapper>
                <GmailCallbackPage />
              </SuspenseWrapper>
            ),
          },

          // Analytics
          {
            path: '/reports',
            element: (
              <SuspenseWrapper>
                <ReportsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/calendar',
            element: (
              <SuspenseWrapper>
                <CalendarPage />
              </SuspenseWrapper>
            ),
          },

          // Email Templates (visible to all users)
          {
            path: '/email-templates',
            element: (
              <SuspenseWrapper>
                <EmailTemplatesPage />
              </SuspenseWrapper>
            ),
          },

          // Automation
          {
            path: '/workflows',
            element: (
              <SuspenseWrapper>
                <WorkflowsPage />
              </SuspenseWrapper>
            ),
          },

          // Notifications
          {
            path: '/notifications',
            element: (
              <SuspenseWrapper>
                <NotificationsPage />
              </SuspenseWrapper>
            ),
          },

          // Profile
          {
            path: '/profile',
            element: (
              <SuspenseWrapper>
                <ProfilePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/profile/:id',
            element: (
              <SuspenseWrapper>
                <ProfilePage />
              </SuspenseWrapper>
            ),
          },

          // Admin routes (restricted to admin roles)
          {
            element: <AdminRoute />,
            children: [
              {
                path: '/admin/users',
                element: (
                  <SuspenseWrapper>
                    <AdminUsersPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/admin/roles',
                element: (
                  <SuspenseWrapper>
                    <AdminRolesPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/admin/teams',
                element: (
                  <SuspenseWrapper>
                    <AdminTeamsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/admin/assignment-rules',
                element: (
                  <SuspenseWrapper>
                    <AssignmentRulesPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/admin/settings',
                element: (
                  <SuspenseWrapper>
                    <AdminSettingsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/admin/automation',
                element: (
                  <SuspenseWrapper>
                    <AutomationRulesPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/admin/workflows',
                element: (
                  <SuspenseWrapper>
                    <WorkflowDefinitionsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/admin/audit-logs',
                element: (
                  <SuspenseWrapper>
                    <AuditLogsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/admin/email-templates',
                element: (
                  <SuspenseWrapper>
                    <EmailTemplatesPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },
        ],
      },
    ],
  },

  // 404 page
  {
    path: '/404',
    element: <NotFoundPage />,
  },

  // Catch-all → 404
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
])

export default router
